/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { RadioGroupProps } from '../../types';
import Radio from './Radio';
import RadioGroupContext from './RadioGroupContext';

const RadioGroup = ({
  name,
  value,
  defaultValue,
  onChange,
  disabled,
  options,
  direction = 'vertical',
  className,
  children,
}: RadioGroupProps) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? '');

  const selectedValue = isControlled ? value! : internalValue;

  const onGroupChange = React.useCallback(
    (val: string) => {
      if (!isControlled) setInternalValue(val);
      onChange?.(val);
    },
    [isControlled, onChange]
  );

  const contextValue = React.useMemo(() => ({ name, selectedValue, onGroupChange, disabled }), [
    name,
    selectedValue,
    onGroupChange,
    disabled,
  ]);

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <div className={cx('radio-group', `radio-group--${direction}`, className)} role="radiogroup">
        {options
          ? options.map((opt) => (
              <Radio key={opt.value} value={opt.value} disabled={opt.disabled} description={opt.description}>
                {opt.label}
              </Radio>
            ))
          : children}
      </div>
    </RadioGroupContext.Provider>
  );
};

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
